import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { isEmpty, get as objectGet } from 'lodash';
import {
  GoogleMap, Polygon
} from '@react-google-maps/api';
import { debounce } from 'lodash';

import {
  boundsFromMultiplePolygons,
  fetchGeoJSONFromNominatim,
  placeBounds,
  polygonFromBounds,
  splitFullAddress
} from '../helpers/map_helpers';
import { addBound, addGeoJson, replaceMapBounds, clearBounds } from 'bundles/connect/components/store/actions/criteria';
import { mapLoaded, setMapZoomReadonly } from 'bundles/connect/components/store/actions/ui';
import { propertyIdOffMarketPropertySearch } from 'bundles/connect/helpers/prospect_api';
import { setOffMarketPropertyId } from 'bundles/connect/components/store/actions/off_market_property';
import { USA_CENTER, USA_BOUNDING_BOX } from 'bundles/connect/constants';
import MapMarkers from './map/map_markers';
import InfoWindow from './map/info_window';
import SearchThisArea from './map/search_this_area';
import CustomMapControls from './map/custom_map_controls';
import { search } from 'bundles/connect/components/store/actions/results';
import PropertyCountOverlay from './map/property_count_overlay';
import CustomMapTypeControl from './map/custom_map_type_control';
import { selectStaticCallList } from 'bundles/connect/components/store/selectors/ui';
import type { RootState } from '../../store/root_reducer';
import type { Criteria } from 'bundles/connect/types';

const DEFAULT_LOCATIONS_ARRAY = [];
const POLYGON_STYLES = {
  fillOpacity: 0,
  strokeColor: '#8a1bd9',
  strokeOpacity: 0.9,
  strokeWeight: 3
};
const MAP_CONTAINER_STYLES = { width: '100%', height: '100%' };

type MapProps = {
  addBound: (bounds: Array<{ lat: number; lng: number }>, options?: { name: string }) => void;
  addGeoJson: (geoJson: {
    type: string;
    label: string;
    coordinates: number[][][]
  }, properties: {
    name: string;
  }) => void;
  allResults: RootState['results']['offMarketResults'] | RootState['results']['allResults'];
  clearBounds: () => void;
  defaultMapType: string;
  locations: Criteria['locationsAttributes'];
  mapBounds?: Criteria['mapBounds'];
  mapLoaded: () => void;
  nominatimUrl: string;
  replaceMapBounds: (bounds: { lat: number; lng: number }[]) => void;
  search: () => void;
  setMapZoomReadonly: (zoom: number) => void;
  setOffMarketPropertyId: (id: string) => void;
  showMap: boolean;
  showTableView: boolean;
  updateResultsOnMapMove: boolean;
}

function Map({
  addBound,
  addGeoJson,
  allResults,
  clearBounds,
  defaultMapType,
  locations,
  mapBounds,
  mapLoaded,
  nominatimUrl,
  replaceMapBounds,
  search,
  setMapZoomReadonly,
  setOffMarketPropertyId,
  showMap,
  showTableView,
  updateResultsOnMapMove
}: MapProps) {
  const staticCallList = useSelector(selectStaticCallList);
  const [drawingMode, setDrawingMode] = useState<string | null>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const mapOptions = useMemo(() => ({
    clickableIcons: false,
    fullscreenControl: false,
    mapId: '5de8e726806581b',
    mapTypeControl: false,
    minZoom: 4,
    streetViewControl: false,
    cameraControl: false
  }), []);

  const polygons = useMemo(() => {
    const polygonsArray: Array<Array<{ lat: number; lng: number }>> = [];
    (locations || []).forEach((location) => {
      if (!location.polygonGeojson) return;

      if (location.polygonGeojson.type == 'Polygon') {
        const coords = location.polygonGeojson.coordinates;
        if (coords[0]) polygonsArray.push(coords[0].map(point => ({ lat: point[1], lng: point[0] })));
      } else if (location.polygonGeojson.type == 'MultiPolygon') {
        location.polygonGeojson.coordinates.forEach((coords) => {
          if (coords[0]) polygonsArray.push(coords[0].map(point => ({ lat: point[1], lng: point[0] })));
        });
      }
    });

    return polygonsArray;
  }, [locations]);

  const shortDebounceSearch = debounce(() => {
    search();
  }, 500);

  const centerMap = useCallback(() => {
    if (!map) return;

    const { latMin, latMax, lngMin, lngMax } = USA_BOUNDING_BOX;
    const continentalUs = new google.maps.LatLngBounds(
      new google.maps.LatLng(latMin, lngMin),
      new google.maps.LatLng(latMax, lngMax)
    );
    map.fitBounds(continentalUs);
  }, [map]);

  // When a street address is selected, use the trySetPropertyFromAddress action to try and open
  // the property modal directly
  const trySetPropertyFromAddress = useCallback(async (place) => {
    if (!place.types.some(type => ['premise', 'street_address'].includes(type))) return false;

    try {
      const addressParts = splitFullAddress(place.address).slice(0, 3);
      const { id, latitude, longitude } = await propertyIdOffMarketPropertySearch(
        addressParts[0], addressParts[1], addressParts[2]
      );

      setOffMarketPropertyId(id);
      if (map) {
        map.panTo({ lat: latitude, lng: longitude });
        map.setZoom(18);
      }

      return true;
    } catch (e) {
      return false;
    }
  }, [map]);

  const fetchGeoJSONAndPlaceBound = (placeDetails) => {
    fetchGeoJSONFromNominatim(nominatimUrl, placeDetails, {
      successCallback: (geoJson) => {
        addGeoJson(geoJson, { name: placeDetails.displayName });
      },
      noResultCallback: async () => {
        addBound(placeBounds(placeDetails), { name: placeDetails.displayName });
      }
    });
  };

  // When a place is selected, try to lookup the details from nominatim using the google place formatted address.
  // We use the formatted address for things like La County which returns Los Angeles County, CA
  // We don't look up the google formatted address on neighborhoods because that screws up things like Loop Chicago
  // 1. Boundary from nominatim is found
  //   a. Replace the search bounds with the new bounds and fit the map to the bounds
  // 2. Boundary from nominatim IS NOT found
  //   a. Google Place result contains a viewport
  //      i. If using the map view - fit the map to the place viewport and let it auto update the search bounds
  //      ii. If not using the map view - replace the search bounds with the place viewport
  //   b. Google Place result does not contain a viewport
  //      i. If using the map view - pan the map to the place location and let it auto update the search bounds
  //      ii. If not using the map view - set the search bounds to a 10square mile bounding box around the location
  useEffect(() => {
    const placeSelected = async (e) => {
      const selectedPlace = e.detail.place;
      if (await trySetPropertyFromAddress({ ...e.detail, types: selectedPlace.types })) return;

      const { place } = await selectedPlace.toPlace().fetchFields(
        { fields: ['addressComponents', 'formattedAddress', 'displayName', 'location', 'viewport', 'types'] });

      if (selectedPlace.types.includes('neighborhood')) {
        fetchGeoJSONAndPlaceBound(place);
      } else if (selectedPlace.types.includes('postal_code')) {
        addBound(placeBounds(place), { name: place.displayName });
      } else {
        fetchGeoJSONAndPlaceBound(place);
      }
    };

    window.addEventListener('placeSelect', placeSelected);

    return () => {
      window.removeEventListener('placeSelect', placeSelected);
    };
  }, [map, showMap, polygons]);

  // Handle when the clearLocation event is sent from elsewhere in the map.
  // The map has to perform this logic because only it knows it's current bounds and visibility state
  useEffect(() => {
    window.addEventListener('clearLocation', clearBounds);

    return () => {
      window.removeEventListener('clearLocation', clearBounds);
    };
  }, [showMap, map]);

  // On mobile, when flipping between list and map view, recenter the map on any existing bounds
  // On polygon set (region), pan the map to fit
  useEffect(() => {
    if (!showMap || !map) return;

    if (polygons.length > 0) {
      map.fitBounds(boundsFromMultiplePolygons(polygons));
    }
  }, [showMap]);

  useEffect(() => {
    if (map && staticCallList && !isEmpty(allResults)) {
      const latLngObjects = allResults.map(result => ({ lat: result.latitude, lng: result.longitude }));
      const bounds = new google.maps.LatLngBounds();
      latLngObjects.forEach((point) => {
        bounds.extend(new google.maps.LatLng(point.lat, point.lng));
      });
      map.fitBounds(bounds);
    }

  }, [map, allResults, staticCallList]);

  useEffect(() => {
    if (showMap && map && polygons.length > 0) {
      map.fitBounds(boundsFromMultiplePolygons(polygons));
    }
  }, [JSON.stringify(polygons[polygons.length - 1])]);

  const onLoad = useCallback((map) => {
    mapLoaded();
    map.mapTypeId = defaultMapType || 'roadmap';
    setMap(map);

    if (mapBounds) {
      const sw = new google.maps.LatLng(mapBounds.south, mapBounds.west);
      const ne = new google.maps.LatLng(mapBounds.north, mapBounds.east);
      map.fitBounds(new google.maps.LatLngBounds(sw, ne), 0);
    } else if (polygons.length > 0) {
      map.fitBounds(boundsFromMultiplePolygons(polygons));
    } else centerMap();
  }, []);

  const boundsUpdated = useCallback(() => {
    if (!showMap) return;
    if (!map) return;

    const zoom = map.getZoom();
    if (zoom !== undefined) {
      setMapZoomReadonly(zoom);
    }

    replaceMapBounds(
      polygonFromBounds(map.getBounds())
    );
  }, [showMap, map]);

  const searchAreaClick = useCallback(() => {
    shortDebounceSearch();
  }, [map]);

  const handleDrawComplete = useCallback((polygon) => {
    setDrawingMode(null);

    const coordinates: Array<{ lat: number; lng: number }> = [];
    const points = polygon.geometry.coordinates[0];

    points.forEach((point) => {
      const lat = point[1];
      const lng = point[0];

      coordinates.push({ lat, lng });
    });

    addBound(coordinates);
  }, [addBound]);

  return (
    <GoogleMap
      center={USA_CENTER}
      id="search-map"
      mapContainerStyle={MAP_CONTAINER_STYLES}
      options={mapOptions}
      zoom={3}
      onIdle={boundsUpdated}
      onLoad={onLoad}
    >
      {!staticCallList &&
        <CustomMapControls
          drawingMode={drawingMode}
          map={map}
          position={google.maps.ControlPosition.TOP_RIGHT}
          setDrawingMode={setDrawingMode}
          onClear={clearBounds}
          onDrawComplete={handleDrawComplete}
        />}
      <div className="d-flex flex-column m-4 position-absolute h-100 justify-content-between">
        <PropertyCountOverlay />
        <CustomMapTypeControl map={map} />
      </div>
      {showMap && <InfoWindow />}
      {(showMap && !drawingMode) && <MapMarkers />}
      {(showTableView && !updateResultsOnMapMove) && <SearchThisArea searchArea={searchAreaClick} />}
      {polygons.map((polygon, idx) => (
        <Polygon
          key={idx}
          options={{
            ...POLYGON_STYLES,
            clickable: false,
            draggable: false,
            editable: false,
            geodesic: false,
            zIndex: 1
          }}
          paths={polygon}
        />
      ))}
    </GoogleMap>
  );
}

const mapStateToProps = ({ criteria,
  results,
  ui: {
    offMarketSearch,
    updateResultsOnMapMove,
    showTableView,
    profile: { defaultMapType }
  } }) => {
  const { mapBounds } = criteria;
  const uiState = { updateResultsOnMapMove, showTableView };
  const locations = objectGet(criteria, 'locationsAttributes') || DEFAULT_LOCATIONS_ARRAY;

  const allResults = (offMarketSearch ? results.allOffMarketResults : results.allResults) || [];
  // In the case that there are no locations, use DEFAULT_LOCATIONS_ARRAY. By using the same object we can avoid
  // re-renders. Without using DEFAULT_LOCATIONS_ARRAY, react would see [] as a new object each time and rerender
  // the map everytime the redux store is updated.
  return { allResults, locations, mapBounds, defaultMapType, ...uiState };
};

export default connect(
  mapStateToProps,
  {
    addBound,
    addGeoJson,
    clearBounds,
    mapLoaded,
    replaceMapBounds,
    setMapZoomReadonly,
    setOffMarketPropertyId,
    search,
  }
)(Map);
